import { StatisticalAnalysisSidebarBase, StatisticalAnalysisSidebarItemCombo } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar/statistical-analysis-sidebar.models'
import { uniqueId } from 'lodash'
import { StatisticalAnalysisHeaderNavigationItems, StatisticalAnalysisHeaderValues, StatisticalAnalysisTypeNavigation } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-header/statistical-analysis-header.models'
import { StatisticalAnalysisDossiers, StatisticalAnalysisEnumsAndKeys, StatisticalAnalysisIncidentsTimesTypeSelectorEnum, StatisticalAnalysisIncidentTypes, StatisticalAnalysisViewDetailParams } from '@/modules/statistical-analysis-module/shared/models/shared.model'
import { Nullable } from '@/shared/types/app.types'
import { StatisticalAnalysisTemporalFilterEnum, StatisticalAnalysisTemporalFilterOptions } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-temporal-filter/statistical-analysis-temporal-filter.model'
import { LaboratoryStatisticalAnalysisSidebarDateInput } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-sidebar-date-and-centers/statistical-analysis-sidebar-date-and-centers.model'
import i18n from '@/lang/i18n'

export interface StatisticalAnalysisIncidentsDossierModelSearchInfo {
    typeSearch: Nullable<StatisticalAnalysisSidebarItemCombo[]>
    laboratories: Nullable<LaboratoryStatisticalAnalysisSidebarDateInput[]>
    startDate: Nullable<Date>
    endDate: Nullable<Date>
}

export interface StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter {
    id: StatisticalAnalysisIncidentsTimesTypeSelectorEnum,
    text: string
}

export interface StatisticalAnalysisIncidentsDossierExternalInfo extends StatisticalAnalysisIncidentsDossierModelSearchInfo{
    trackingGeneralSelected: Nullable<StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisIncidentTypes>>
    temporalFilter: StatisticalAnalysisTemporalFilterOptions
    typeTimeFilter: StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter
}

export interface StatisticalAnalysisDossierIncidentsGeneralStatistics {
    max: Nullable<number>
    min: Nullable<number>
    median: Nullable<number>
    mean: Nullable<number>
    variation: Nullable<number>
}

export interface StatisticalAnalysisDossierIncidentsState extends StatisticalAnalysisSidebarBase {
    headerData: StatisticalAnalysisHeaderValues
    viewDetail: StatisticalAnalysisViewDetailParams
    externalInfo: StatisticalAnalysisIncidentsDossierExternalInfo
    typeTimeFilterList: StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter[]
    generalStatistics: StatisticalAnalysisDossierIncidentsGeneralStatistics
    promiseNavigationItems: Nullable<Promise<void>>
    promiseStatistics: Nullable<Promise<void>>
    loading: boolean
}

const StatisticalAnalysisIncidentsStatisticsDossierAxisXList = [
    StatisticalAnalysisEnumsAndKeys.INCIDENT_ORIGIN.getModel(),
    StatisticalAnalysisEnumsAndKeys.INCIDENT_TYPE.getModel(),
    StatisticalAnalysisEnumsAndKeys.INCIDENT_SEVERITY.getModel(),
    StatisticalAnalysisEnumsAndKeys.CORE_ENTITY.getModel(),
    StatisticalAnalysisEnumsAndKeys.OPENED_BY.getModel(),
    StatisticalAnalysisEnumsAndKeys.CLOSED_BY.getModel(),
    StatisticalAnalysisEnumsAndKeys.PHASE.getModel()
]

export const navigationItems = ():StatisticalAnalysisHeaderNavigationItems<StatisticalAnalysisIncidentTypes>[] => {
    return [
        {
            label: i18n.tc('statisticalAnalysis.dossiers.incidents.status.occurred'),
            value: 0,
            key: StatisticalAnalysisIncidentTypes.OCCURRED,
            icon: 'fal fa-list-ul'
        },
        {
            label: i18n.tc('statisticalAnalysis.dossiers.incidents.status.open'),
            value: 0,
            key: StatisticalAnalysisIncidentTypes.OPENED,
            icon: 'fal fa-exclamation-circle'
        },
        {
            label: i18n.tc('statisticalAnalysis.dossiers.incidents.status.closed'),
            value: 0,
            key: StatisticalAnalysisIncidentTypes.CLOSED,
            icon: 'fal fa-times-circle'
        }
    ]
}

export const initialIncidentStatisticsDossierState =
    (): StatisticalAnalysisDossierIncidentsState => ({
        sidebar: {
            title: 'statisticalAnalysis.sections.incidents',
            id: 'incidents',
            components: [
                {
                    name: 'StatisticalAnalysisSidebarSearchBy',
                    id: uniqueId(),
                    props: {
                        multiple: true,
                        options: [
                            StatisticalAnalysisEnumsAndKeys.PATIENT.getModel(),
                            StatisticalAnalysisEnumsAndKeys.REQUEST.getModel(),
                            StatisticalAnalysisEnumsAndKeys.CASE.getModel(),
                            StatisticalAnalysisEnumsAndKeys.SAMPLE.getModel(),
                            StatisticalAnalysisEnumsAndKeys.PORTION.getModel(),
                            StatisticalAnalysisEnumsAndKeys.SUBPORTION.getModel(),
                            StatisticalAnalysisEnumsAndKeys.GENERAL.getModel()
                        ],
                        preselection: [
                            StatisticalAnalysisEnumsAndKeys.CASE.getModel()
                        ]
                    }
                },
                {
                    name: 'StatisticalAnalysisSidebarDateAndCenters',
                    id: uniqueId(),
                    props: { }
                }
            ]
        },
        headerData: {
            header: {
                total: 0,
                showTotal: false,
                startDate: new Date(),
                endDate: new Date(),
                elementType: 'statisticalAnalysis.dossiers.incidents.incidentResults'
            },
            navigationItems: [],
            navigationType: StatisticalAnalysisTypeNavigation.NAVIGATION,
            styles: {
                background: require('@/img/incident-statistics-bg.jpg'),
                icon: 'far fa-p-triangle'
            },
            optionsSlider: {
                slidesPerView: 3,
                spaceBetween: 40,
                slidesPerGroup: 1,
                breakpoints: {
                    1024: {
                        slidesPerView: 3,
                        spaceBetween: 40
                    },
                    768: {
                        slidesPerView: 3,
                        spaceBetween: 30
                    },
                    640: {
                        slidesPerView: 2,
                        spaceBetween: 20
                    },
                    320: {
                        slidesPerView: 1,
                        spaceBetween: 10
                    }
                }
            }
        },
        viewDetail: {
            dossier: StatisticalAnalysisDossiers.INCIDENTS,
            table: {
                contextMenuItems: {
                    detailChart: false,
                    detailList: true
                },
                temporalFilter: {
                    isShow: false
                }
            },
            chart: {
                axis: {
                    xAxis: {
                        show: true,
                        mainCombo: {
                            list: StatisticalAnalysisIncidentsStatisticsDossierAxisXList,
                            selected: StatisticalAnalysisIncidentsStatisticsDossierAxisXList[0]
                        },
                        associatedCombo: {
                            list: StatisticalAnalysisIncidentsStatisticsDossierAxisXList,
                            selected: StatisticalAnalysisIncidentsStatisticsDossierAxisXList[1]
                        }
                    },
                    yAxis: {
                        show: false
                    }
                },
                showExtendedFilters: false
            }
        },
        externalInfo: {
            typeSearch: [],
            laboratories: [],
            startDate: null,
            endDate: null,
            trackingGeneralSelected: null,
            temporalFilter: {
                id: StatisticalAnalysisTemporalFilterEnum.DAYS,
                text: 'statisticalAnalysis.times.days'
            },
            typeTimeFilter: StatisticalAnalysisEnumsAndKeys.OPENING.getModel() as StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter
        },
        typeTimeFilterList: [
            StatisticalAnalysisEnumsAndKeys.OPENING.getModel() as StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter,
            StatisticalAnalysisEnumsAndKeys.CLOSING.getModel() as StatisticalAnalysisIncidentsDossierExternalInfoTypeTimeFilter
        ],
        generalStatistics: {
            max: null,
            mean: null,
            median: null,
            min: null,
            variation: null
        },
        loading: false,
        promiseNavigationItems: null,
        promiseStatistics: null
    })
